import React, { useEffect, useRef, useState } from 'react';

/**
 * Loads images that may require auth (attendance selfies, etc.)
 * Tries primary URL first, then optional fallbacks, using fetch + blob URLs.
 */
const AuthenticatedImage = ({
  src,
  fallbackSrc,
  alt = '',
  className = '',
  onLoadError,
}) => {
  const [displaySrc, setDisplaySrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    const loadImage = async () => {
      setLoading(true);
      setDisplaySrc(null);

      const candidates = [src, fallbackSrc].filter(Boolean);
      const token = localStorage.getItem('erp_auth_token');

      for (const url of candidates) {
        try {
          const headers = {};
          const needsAuth = url.includes('/api/') || url.includes('/attendance/');
          if (needsAuth && token) {
            headers.Authorization = `Bearer ${token}`;
          }

          const response = await fetch(url, { headers });
          if (!response.ok) continue;

          const contentType = response.headers.get('content-type') || '';
          const isImage =
            contentType.startsWith('image/') ||
            contentType.includes('octet-stream') ||
            !contentType;

          if (!isImage) continue;

          const blob = await response.blob();
          if (cancelled || !blob.size) continue;

          // Reject JSON/HTML error bodies masquerading as images
          if (blob.type && !blob.type.startsWith('image/') && blob.type !== 'application/octet-stream') {
            continue;
          }

          objectUrl = URL.createObjectURL(blob);
          setDisplaySrc(objectUrl);
          setLoading(false);
          return;
        } catch {
          // try next candidate
        }
      }

      if (!cancelled) {
        setLoading(false);
        onLoadErrorRef.current?.();
      }
    };

    loadImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, fallbackSrc]);

  if (loading) {
    return <div className={`${className} bg-muted animate-pulse`} aria-hidden="true" />;
  }

  if (!displaySrc) return null;

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={className}
    />
  );
};

export default AuthenticatedImage;
